import axios from 'axios';
import { AxiosRequestConfig } from "axios";

/**
* Sorts an array of objects by a specified key in ascending or descending order.
* @param {Object[]} arr - The array of objects to sort.
* @param {string} key - The key to sort by.
* @param {boolean} ascending - Flag indicating the sort order.
*   - If true, sorts in ascending order.
*   - If false, sorts in descending order.
* @returns {Object[]} - The sorted array of objects.
*/
export function sortObjectsByKey(arr: any[], key: string, ascending = true) {
  return arr.slice().sort((a, b) => {
    const valA = a[key];
    const valB = b[key];
 
    if (typeof valA === 'string' && typeof valB === 'string') {
      // Compare strings
      if (ascending) {
        return valA.localeCompare(valB);
      } else {
        return valB.localeCompare(valA);
      }
    } else {
      // Compare other types (number, etc.)
      if (ascending) {
        return valA > valB ? 1 : valA < valB ? -1 : 0;
      } else {
        return valA < valB ? 1 : valA > valB ? -1 : 0;
      }
    }
  });
}
 
export interface APIInterface {
  setAuthentication(auth: any, url: any): void
  post(url: string, data: any): Promise<any>
  put(url: string, data: any): Promise<any>
  get(url: string): Promise<any>
  delete(url: string): Promise<any>
  uploadDocument(file: File): Promise<any>
  uploadLargeFile(url: string, formData: FormData): Promise<any>
}
 
export interface ImportColumn {
  name: string
  comArray?: boolean
  numType?: boolean
  boolType?: boolean
  column: string
  required?: boolean
  dateType?: boolean
  // Which column to use from 
  // excel.
  targetColumn?: number
}
 
const w = window as any
const $ = w["$"] as any
let loaderCount = 0
 
function hideLoader() {
  loaderCount--
 
  if (loaderCount <= 0) {
    $(".pageloader").fadeOut()
    loaderCount = 0
  }
}
 
export class APIClient {
  private instance: any
  private basePath: string
 
  constructor(auth: any = {}, url: any = process.env.VUE_APP_API_URL) {
    this.setAuthentication(auth, url)
    this.basePath = url
  }
 
  public setAuthentication(auth: any, url: any): void {
    if (url) {
      this.basePath = url
    }
 
    auth.username = window.localStorage["email"]
 
    this.instance = axios.create({
      baseURL: this.basePath,
      timeout: 180000,
      headers: auth,
      withCredentials: true,
    });
 
    this.instance.interceptors.request.use(function (config: any) {
      // Do something before request is sent
      if (!loaderCount) {
        $(".pageloader").fadeIn()
        loaderCount++
      }
      console.log(JSON.stringify(config))
      return config;
    }, function (error: any) {
      // Do something with request error
      return Promise.reject(error);
    });
 
    this.instance.interceptors.response.use(function (response: any) {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      hideLoader()
      return response;
    }, function (error: any) {
      hideLoader()
      if (error?.response?.status == 401) {
        window.localStorage.clear()
        window.location = "/login" as any
        return
      }
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      return Promise.reject(error);
    });
  }
 
  public async uploadLargeFile(url: string, formData: FormData): Promise<any> {
    // Create a new instance specifically for large file uploads
    const uploadInstance = axios.create({
      baseURL: this.basePath,
      timeout: 0,  // No timeout
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      headers: {
        ...this.instance.defaults.headers,
        'Content-Type': 'multipart/form-data'
      },
      withCredentials: true
    });
 
    // Add request interceptor for loader
    uploadInstance.interceptors.request.use(function (config: any) {
      if (!loaderCount) {
        $(".pageloader").fadeIn()
        loaderCount++
      }
      return config;
    }, function (error: any) {
      hideLoader()
      return Promise.reject(error);
    });
 
    // Add response interceptor for loader and auth
    uploadInstance.interceptors.response.use(function (response: any) {
      hideLoader()
      return response;
    }, function (error: any) {
      hideLoader()
      if (error?.response?.status == 401) {
        window.localStorage.clear()
        window.location = "/login" as any
        return
      }
      return Promise.reject(error);
    });
 
    return uploadInstance.post(url, formData);
  }
 
  public async uploadDocument(file: File) {
    const formData = new FormData();
    formData.append("document", file);
 
    return this.instance.post("/document", formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
 
  public async post(url: string, data: any, config?: AxiosRequestConfig): Promise<any> {
    return this.instance.post(url, data, config);
  }
 
  public async put(url: string, data: any): Promise<any> {
    return this.instance.put(url, data)
  }
 
  public async get(url: string, opts: any = null): Promise<any> {
    return this.instance.get(url, opts)
  }
 
  public async delete(url: string): Promise<any> {
    return this.instance.delete(url)
  }
}
 
export const Client = new APIClient()